import {
  PlaytestApiKeyRotateResultSchema,
  PlaytestApiKeyRowSchema,
  type PlaytestApiKey,
  type PlaytestApiKeyRotateResult,
} from '@playerbase/types';

import { supabase } from '../lib/supabase.js';

// ---------------------------------------------------------------------------
// playtest_api_keys data layer.
//
// Reads via PostgREST (RLS limits SELECT to members; column-level grants
// hide the `key` column from authenticated, so the row we receive carries
// metadata only). Writes via rotate_playtest_api_key - which is the only
// time the plaintext ever leaves the database, and only to the caller of
// that one RPC.
// ---------------------------------------------------------------------------

const TABLE = 'playtest_api_keys';

export async function fetchPlaytestApiKey(
  gameId: string,
): Promise<PlaytestApiKey | null> {
  const { data, error } = await supabase
    .from(TABLE)
    .select('id, game_id, studio_id, key_prefix, created_by_user_id, created_at, rotated_at')
    .eq('game_id', gameId)
    .maybeSingle();
  if (error !== null) {
    console.warn('[playtest-api-key] Failed to load:', error.message);
    return null;
  }
  if (data === null) return null;
  const parsed = PlaytestApiKeyRowSchema.safeParse(data);
  if (!parsed.success) {
    console.warn('[playtest-api-key] Could not parse row.', parsed.error.issues);
    return null;
  }
  return parsed.data;
}

export type PlaytestApiKeyRpcError =
  | 'not_authenticated'
  | 'not_a_member'
  | 'game_not_found'
  | 'game_id_required'
  | 'unknown';

export interface PlaytestApiKeyRpcResult<T> {
  ok: boolean;
  data: T | null;
  error: PlaytestApiKeyRpcError | null;
}

function classifyError(message: string | null | undefined): PlaytestApiKeyRpcError {
  if (typeof message !== 'string') return 'unknown';
  if (message.includes('not_authenticated')) return 'not_authenticated';
  if (message.includes('not_a_member')) return 'not_a_member';
  if (message.includes('game_not_found')) return 'game_not_found';
  if (message.includes('game_id_required')) return 'game_id_required';
  return 'unknown';
}

export async function rotatePlaytestApiKey(
  gameId: string,
): Promise<PlaytestApiKeyRpcResult<PlaytestApiKeyRotateResult>> {
  const { data, error } = await supabase.rpc('rotate_playtest_api_key', {
    p_game_id: gameId,
  });
  if (error !== null) {
    console.warn('[playtest-api-key] rotate failed:', error.message);
    return { ok: false, data: null, error: classifyError(error.message) };
  }
  // The RPC returns a setof row; the postgrest client may yield it as an
  // array (when called via .rpc on a setof-returning function) or a
  // single row.
  const row = Array.isArray(data) ? data[0] : data;
  const parsed = PlaytestApiKeyRotateResultSchema.safeParse(row);
  if (!parsed.success) {
    return { ok: false, data: null, error: 'unknown' };
  }
  return { ok: true, data: parsed.data, error: null };
}
